import { formatCopy } from '../../copy/format.js';
import copy from '../../copy/en/common.json' with { type: 'json' };
/**
 * Display helpers shared by every screen: dates, money, field values, and the
 * label/tone maps for signing, delivery, onboarding and link statuses. Pure
 * data in, strings/tones out — no markup — so React components and tests can
 * both use them. Form/field titles come from the git-versioned manifests in
 * `@ohf/core/form-display`.
 */
import {
  DEFAULT_ONBOARDING_GROUP_KEY,
  fieldLabel,
  formShortTitle,
  formTitle,
  onboardingDisplayTitle,
  onboardingGroupTitle,
} from "@ohf/core/form-display";

export { fieldLabel, formShortTitle, formTitle, onboardingDisplayTitle, onboardingGroupTitle };

export type Tone = "neutral" | "success" | "warning" | "danger" | "info" | "accent";

export const DEFAULT_ONBOARDING_TITLE = onboardingGroupTitle(DEFAULT_ONBOARDING_GROUP_KEY);

/** Compact onboarding-list label — short titles, not the legal packet name. */
export function onboardingListTitle(opts: { sequence: string[]; implicit?: boolean }): string {
  if (opts.implicit || opts.sequence.length <= 1) {
    return formShortTitle(opts.sequence[0] ?? "");
  }
  return formSequenceLabel(opts.sequence);
}

export function formSequenceLabel(sequence: string[]): string {
  return sequence.map(formShortTitle).join(" → ") || "—";
}

// ---- Dates & money ---------------------------------------------------------

const dateTimeFmt = new Intl.DateTimeFormat(undefined, {
  year: "numeric",
  month: "short",
  day: "2-digit",
  hour: "2-digit",
  minute: "2-digit",
});
const dateOnlyFmt = new Intl.DateTimeFormat(undefined, {
  year: "numeric",
  month: "short",
  day: "2-digit",
});

/** Local-time `Sep 02, 2026, 11:38 PM`; `—` for missing/invalid. */
export function fmtDate(iso?: string | null, opts: { time?: boolean } = {}): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return (opts.time === false ? dateOnlyFmt : dateTimeFmt).format(d);
}

/** Relative wording for timelines (`3 min ago`, `2 days ago`); falls back to fmtDate. */
export function fmtRelative(iso?: string | null, now: Date = new Date()): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  const diffSec = Math.round((now.getTime() - d.getTime()) / 1000);
  const abs = Math.abs(diffSec);
  const rtf = new Intl.RelativeTimeFormat(undefined, { numeric: "auto" });
  if (abs < 60) return rtf.format(-diffSec, "second");
  if (abs < 3600) return rtf.format(-Math.round(diffSec / 60), "minute");
  if (abs < 86_400) return rtf.format(-Math.round(diffSec / 3600), "hour");
  if (abs < 86_400 * 30) return rtf.format(-Math.round(diffSec / 86_400), "day");
  return fmtDate(iso, { time: false });
}

/** Integer cents → `$1,234.56` (USD only for now, per the conventions). */
export function fmtMoney(cents: number | null | undefined, currency = "USD"): string {
  if (cents === null || cents === undefined || !Number.isFinite(cents)) return "—";
  return new Intl.NumberFormat(undefined, { style: "currency", currency }).format(cents / 100);
}

export function fmtNumber(n: number | null | undefined): string {
  if (n === null || n === undefined || !Number.isFinite(n)) return "—";
  return new Intl.NumberFormat().format(n);
}

// ---- Collected fields ------------------------------------------------------

/** True when a collected field should render (arrays included; empty skipped). */
export function fieldHasDisplayValue(value: unknown): boolean {
  if (Array.isArray(value)) {
    return value.some((v) => String(v ?? "").trim() !== "");
  }
  return String(value ?? "").trim() !== "";
}

/** Display-only US phone when the stored value is 10 or 11 digits. */
export function formatFieldValue(fieldKey: string, value: unknown): string {
  const raw = Array.isArray(value)
    ? value.map((v) => String(v ?? "").trim()).filter(Boolean).join(", ")
    : String(value ?? "");
  if (fieldKey !== "phone" && fieldKey !== "mobilePhone") return raw;
  const digits = raw.replace(/\D/g, "");
  const national = digits.length === 11 && digits.startsWith("1") ? digits.slice(1) : digits;
  if (national.length !== 10) return raw;
  return `(${national.slice(0, 3)}) ${national.slice(3, 6)}-${national.slice(6)}`;
}

/** Title-case a snake_case key when no explicit label exists. */
export function humanizeKey(value: unknown): string {
  const raw = String(value ?? "").trim();
  if (!raw) return "—";
  return raw
    .split(/[_-]+/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
}

// ---- Signing statuses ------------------------------------------------------

export const STATUS_TONE: Record<string, Tone> = {
  finalized: "success",
  completed: "success",
  signed: "info",
  consented: "info",
  review_generated: "warning",
  data_completed: "warning",
  opened: "warning",
  active: "warning",
  created: "neutral",
  page_visit: "neutral",
};

/** Staff-facing labels. Keys stay available for tooltips — do not invent new meaning. */
export const STATUS_LABEL: Record<string, string> = {
  finalized: copy.format.finalized,
  completed: copy.format.completed,
  signed: copy.format.signed,
  consented: copy.format.consented,
  review_generated: copy.format.previewGenerated,
  data_completed: copy.format.dataCompleted,
  opened: copy.format.opened,
  active: copy.format.inProgress,
  created: copy.format.created,
  page_visit: copy.format.pageVisit,
};

export function statusLabel(status: string): string {
  return STATUS_LABEL[status] ?? humanizeKey(status);
}

export function statusTone(status: string): Tone {
  return STATUS_TONE[status] ?? "neutral";
}

/** Submissions status dropdown — visit-only (`created` / `opened`) omitted. */
export const SIGNING_STATUS_FILTERS: Array<{ value: string; label: string }> = [
  { value: "data_completed", label: STATUS_LABEL.data_completed },
  { value: "review_generated", label: STATUS_LABEL.review_generated },
  { value: "consented", label: STATUS_LABEL.consented },
  { value: "signed", label: STATUS_LABEL.signed },
  { value: "finalized", label: STATUS_LABEL.finalized },
];

// ---- Email delivery --------------------------------------------------------

export const DELIVERY_TONE: Record<string, Tone> = {
  delivered: "success",
  accepted: "info",
  sending: "info",
  pending: "warning",
  retry_pending: "warning",
  hard_bounced: "danger",
  complained: "danger",
  failed: "danger",
};

export const DELIVERY_LABEL: Record<string, string> = {
  delivered: copy.format.delivered,
  accepted: copy.format.accepted,
  sending: copy.format.sending,
  pending: copy.format.pending,
  retry_pending: copy.format.retryPending,
  hard_bounced: copy.format.hardBounced,
  complained: copy.format.complained,
  failed: copy.format.failed,
};

export function deliveryLabel(status: string): string {
  return DELIVERY_LABEL[status] ?? humanizeKey(status);
}

export function deliveryTone(status: string): Tone {
  return DELIVERY_TONE[status] ?? "neutral";
}

// ---- Tracked links ---------------------------------------------------------

export const CHANNEL_LABEL: Record<string, string> = {
  qr: "QR",
  email: copy.format.email,
  print: copy.format.print,
  portal: copy.format.portal,
};

export function channelLabel(channel?: string | null): string {
  const raw = String(channel ?? "").trim();
  if (!raw) return "—";
  return CHANNEL_LABEL[raw] ?? humanizeKey(raw);
}

export const LINK_STATUS_TONE: Record<string, Tone> = {
  active: "success",
  expired: "neutral",
  revoked: "danger",
};

export const LINK_STATUS_LABEL: Record<string, string> = {
  active: copy.format.active,
  expired: copy.format.expired,
  revoked: copy.format.revoked,
};

export function linkStatusLabel(status: string): string {
  return LINK_STATUS_LABEL[status] ?? humanizeKey(status);
}

export function linkStatusTone(status: string): Tone {
  return LINK_STATUS_TONE[status] ?? "neutral";
}

export function destinationLabel(d: { type?: string; formKey?: string } | null | undefined): string {
  if (!d || typeof d.type !== "string") return copy.format.privateSigningLink;
  if (d.type === "form_start") return formatCopy(copy.format.startForm, { form: formShortTitle(d.formKey ?? "agreement") });
  if (d.type === "form_resume") return copy.format.resume;
  if (d.type === "bundle") return copy.format.bundle;
  if (d.type === "url") return "URL";
  if (d.type === "private") return copy.format.privateSigningLink;
  return humanizeKey(d.type);
}

// ---- Users -----------------------------------------------------------------

export const USER_STATUS_LABEL: Record<string, string> = {
  active: copy.format.active,
  disabled: copy.format.disabled,
  invited: copy.format.invited,
};

export function userStatusLabel(status: string): string {
  return USER_STATUS_LABEL[status] ?? humanizeKey(status);
}

export function userStatusTone(status: string): Tone {
  if (status === "active") return "success";
  if (status === "disabled") return "danger";
  if (status === "invited") return "info";
  return "neutral";
}

/** Filesystem-safe slug for downloads. */
export function slugify(input: string, fallback = "file", max = 80): string {
  const s = input
    .trim()
    .replace(/[^\w.-]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, max);
  return s || fallback;
}
